import React, { ForwardedRef, forwardRef, useState, useEffect } from 'react';
import { actions } from '@ecosystem/newron-design-system';
import { Cells, Table } from '../../design/table2';
import { DataColumn } from '../../types';
import { ChartData } from '../data/common';
import { Loading } from '../../design/loading/Loading';
import { useMobile } from '../../context/mobile/mobile.provider';
import classNames from 'classnames';

type Props = {
  periodCount: number;
  data: ChartData[];
  xColumn: DataColumn;
  isEnrichmentLoading: boolean;
  chartFullscreen?: boolean;
};
const MultiKpiTable = forwardRef<HTMLDivElement, Props>(
  ({ data, xColumn, periodCount, isEnrichmentLoading, chartFullscreen }, ref: ForwardedRef<HTMLDivElement>) => {
    const [rowsToDisplay, setRowsToDisplay] = useState<number>(100);
    const [isLandscape, setIsLandscape] = useState(
      typeof window !== 'undefined' && window.innerWidth > window.innerHeight
    );
    const firstData = data.filter(d => d.hasData)[0];
    const rowNames = firstData?.dataSets.map(s => s.label);
    const rowSubLabels = firstData?.dataSets.map(s => s.subLabel);
    const rowCount = firstData?.dataSets.length;
    const includeTotals = firstData?.totalsDataSet !== undefined;

    const handleLoadMore = () => {
      const newAmount = rowsToDisplay + 100;
      if (newAmount > rowCount) {
        setRowsToDisplay(rowCount);
      } else {
        setRowsToDisplay(newAmount);
      }
    };

    useEffect(() => {
      const handleOrientationChange = () => {
        setIsLandscape(window.innerWidth > window.innerHeight);
      };

      window.addEventListener('resize', handleOrientationChange);
      window.addEventListener('orientationchange', handleOrientationChange);

      return () => {
        window.removeEventListener('resize', handleOrientationChange);
        window.removeEventListener('orientationchange', handleOrientationChange);
      };
    }, []);

    const { isMobile } = useMobile();

    const { Button } = actions;

    return (
      <div
        ref={ref}
        className={classNames('relative w-full', {
          'chart-h-70vh mt-7': isMobile && !(isLandscape || chartFullscreen),
          'chart-h-90vh mt-7': isMobile && (isLandscape || chartFullscreen),
        })}
      >
        <Table>
          <Table.Header withRows={false} isStickyTop={isMobile}>
            <tr>
              <Table.Column rowSpan={2} isStickyLeft={isMobile}>
                {xColumn?.label}
              </Table.Column>
              {data.map((set, idx) => (
                <KpiHeadersTop key={`ht_${idx}`} series={set} periodCount={periodCount} />
              ))}
            </tr>
            <tr>
              {data.map((set, idx) => (
                <KpiHeadersBottom
                  key={`ht_${idx}`}
                  series={set}
                  isLoading={isEnrichmentLoading}
                  periodCount={periodCount}
                />
              ))}
            </tr>
          </Table.Header>
          <Table.Body>
            {includeTotals && (
              <Table.Row important>
                <Table.Cell align="left" isStickyLeft={isMobile}>
                  Market
                </Table.Cell>
                {data.map((set, idx) => (
                  <KpiCells key={`sum_${idx}`} series={set} rowIndex="totals" periodCount={periodCount} />
                ))}
              </Table.Row>
            )}
            {rowNames?.slice(0, rowsToDisplay).map((rowName, rIdx) => (
              <Table.Row key={`r_${rowName}`}>
                <Table.Cell align="left" isStickyLeft={isMobile}>
                  <>
                    <span>{rowName}</span>
                    {rowSubLabels[rIdx] && <span className="text-xs text-lighter">{rowSubLabels[rIdx]}</span>}
                  </>
                </Table.Cell>
                {data.map((set, idx) => (
                  <KpiCells
                    key={`r_${rowName}_${rIdx}_${idx}`}
                    series={set}
                    rowIndex={rIdx}
                    periodCount={periodCount}
                  />
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
        <div className="py-4 flex items-center gap-4">
          <span className="">
            {rowsToDisplay > rowCount && `${rowCount} out of ${rowCount} items`}
            {rowsToDisplay <= rowCount && `${rowsToDisplay} out of ${rowCount} items`}
          </span>
          {rowsToDisplay <= rowCount && <Button onClick={handleLoadMore}>Load more</Button>}
        </div>
      </div>
    );
  }
);

type KpiHeadersProps = {
  series: ChartData;
  periodCount: number;
  isLoading?: boolean;
};

function KpiHeadersTop({ series, periodCount }: KpiHeadersProps) {
  return (
    <Table.Column align="center" borders="lrb" colSpan={periodCount}>
      {series.yColumn.label}
    </Table.Column>
  );
}

function KpiHeadersBottom({ series, isLoading, periodCount }: KpiHeadersProps) {
  return (
    <>
      {series.dataLabels.length > 0 ? (
        series.dataLabels.map((label, idx) => (
          <Table.Column
            align="center"
            key={`sr_${idx}`}
            borders={`b${idx === 0 ? 'l' : idx === series.dataLabels.length - 1 ? 'r' : ''}`}
          >
            <HeaderLabel label={label} />
          </Table.Column>
        ))
      ) : (
        <Table.Column align="center" colSpan={periodCount}>
          <Loading isActive={isLoading} withIndicator />
        </Table.Column>
      )}
    </>
  );
}

type LabelProps = {
  label: string | string[];
};
function HeaderLabel({ label }: LabelProps) {
  if (Array.isArray(label)) {
    return (
      <>
        {label.map(l => (
          <span key={l} className="block nowrap">
            {l}
          </span>
        ))}
      </>
    );
  }

  return <span className="block nowrap">{label}</span>;
}

type KpiCellsProps = {
  rowIndex: number | 'totals';
  series: ChartData;
  periodCount: number;
};
function KpiCells({ rowIndex, series, periodCount }: KpiCellsProps) {
  const seriesData = rowIndex === 'totals' ? series?.totalsDataSet : series?.dataSets?.[rowIndex];
  return (
    <>
      {Array(periodCount)
        .fill(null)
        .map((_, idx) => (
          <Table.Cell
            borders={`b${idx === 0 ? 'l' : idx === periodCount - 1 ? 'r' : ''}`}
            key={`row_${rowIndex}_${seriesData?.label}_${idx}`}
          >
            {seriesData?.values?.formatted[idx] || '-'}

            {seriesData && seriesData.changes && (
              <Cells.ValueChange value={seriesData.changes.formatted[idx]} raw={seriesData.changes.raw[idx]} />
            )}
          </Table.Cell>
        ))}
    </>
  );
}

export { MultiKpiTable };
